import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Player } from '../../types/player';
import { User, Activity, TrendingUp, Shield } from 'lucide-react';
import { translatePosition, translateFoot } from '../../utils/positionTranslations';

interface PlayerInfoCardProps {
  player: Player;
  color: string;
}

const PlayerInfoCard: React.FC<PlayerInfoCardProps> = ({ player, color }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as 'es' | 'en';

  const getPlayerName = (player: Player) => {
    const name = player.player_name || player.nombre_transfermarkt || t('comparison.noPlayersFound');
    return name.split('(')[0].trim();
  };

  const getPlayerImage = (player: Player) => {
    return player.player_image_url || player.url_foto_jugador;
  };

  const getAge = () => {
    const birthDate = player.date_of_birth || player.fecha_nacimiento_transfermarkt;
    if (!birthDate) return 'N/A';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    return age;
  };

  const getPosition = () => {
    const position = player.position || player.posicion_transfermarkt || 'N/A';
    return translatePosition(position, currentLang);
  };

  const getFoot = () => {
    const foot = player.foot || player.pie_habil;
    if (!foot) return 'N/A';
    return translateFoot(foot, currentLang);
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return t('players.notAvailable');
    if (amount >= 1000000) {
      return `€${(amount / 1000000).toFixed(1)}M`;
    }
    return `€${(amount / 1000).toFixed(0)}K`;
  };

  const formatContractEnd = () => {
    const contractEnd = player.contract_expires || player.fecha_fin_contrato;
    if (!contractEnd) return t('players.notAvailable');
    const date = new Date(contractEnd);
    return date.toLocaleDateString(currentLang === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const infoSections = [
    {
      icon: User,
      title: t('comparison.basicInfo'),
      items: [
        { label: t('comparison.age'), value: `${getAge()} ${t('landing.playerCard.years').toLowerCase()}` },
        { label: t('comparison.height'), value: player.height || player.altura ? `${player.altura || player.height} cm` : 'N/A' },
        { label: t('comparison.foot'), value: getFoot() },
        { label: t('comparison.nationality'), value: player.citizenship || player.nacion || 'N/A' }
      ]
    },
    {
      icon: Shield,
      title: t('comparison.team'),
      items: [
        { label: t('comparison.team'), value: player.current_club_name || player.equipo_transfermarkt || 'N/A' },
        { label: t('comparison.league'), value: player.competicion || player.nombre_liga || 'N/A' },
        { label: t('comparison.position'), value: getPosition() }
      ]
    },
    {
      icon: Activity,
      title: t('comparison.performance'),
      items: [
        { label: t('comparison.goals'), value: player.goles || 0 },
        { label: t('comparison.assists'), value: player.asistencias || 0 },
        { label: t('comparison.matchesPlayed'), value: player.partidos_jugados || player.partidos || 0 },
        { label: t('comparison.minutesPlayed'), value: player.minutos_jugados || 0 }
      ]
    },
    {
      icon: TrendingUp,
      title: t('comparison.marketValue'),
      items: [
        { label: t('comparison.marketValue'), value: formatCurrency(player.valor_mercado) },
        { label: t('comparison.contract'), value: formatContractEnd() }
      ]
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border-t-4" style={{ borderColor: color }}>
      <div className="flex items-center space-x-4 mb-6">
        <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
          {getPlayerImage(player) ? (
            <img
              src={getPlayerImage(player)}
              alt={getPlayerName(player)}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-12 h-12 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg></div>';
              }}
            />
          ) : (
            <User className="w-12 h-12 text-gray-400" />
          )}
        </div>
        <div className="flex-1">
          <h3 className="text-2xl font-bold text-gray-900">
            {getPlayerName(player)}
          </h3>
          <p className="text-gray-600">
            {player.current_club_name || player.equipo_transfermarkt}
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {infoSections.map((section, index) => {
          const Icon = section.icon;
          return (
            <div key={index}>
              <div className="flex items-center space-x-2 mb-3">
                <Icon className="w-5 h-5" style={{ color }} />
                <h4 className="font-bold text-gray-800">{section.title}</h4>
              </div>
              <div className="grid grid-cols-2 gap-3 ml-7">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex}>
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className="font-semibold text-gray-900">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlayerInfoCard;
